<script setup lang="ts">
import { withDefaults, defineProps } from "vue";
import type {DownloadListItem} from "~/components/download/types";
import type {IModalExpose} from "~/components/types";

const $props = withDefaults(defineProps<{
  items: DownloadListItem[]
}>(), {
  items: () => ([])
})


const downloadModal : Ref<null | IModalExpose> = ref(null);
function onClickLink(e: Event) {
    const href = (e.currentTarget as HTMLLinkElement)?.href;
    if(href.includes("#")) {
      e.preventDefault();
      downloadModal?.value?.open();
    }
}
</script>

<template>
  <ul class="download-list">
    <li class="download-list-item"
      v-for="item in items"
    >
        <div class="download-list-item-icon">
          <nuxt-img
            src="/images/icons/disk.webp"
            alt="disk"
          />
        </div>

        <div class="download-list-item-title">{{ item.name }}</div>

        <ul  v-if="!!item.stats" class="download-list-item-stats">
          <li v-for="(statValue, statName) in item.stats">
            <span class="name">{{ statName }}</span>
            <span class="value">{{ statValue }}</span>
          </li>
        </ul>

        <div class="download-list-item-subtitle"
             v-if="!!item.descriptionTitle"
             v-html="item.descriptionTitle"
        ></div>

        <div class="download-list-item-description"
             v-if="!!item.description"
             v-html="item.description"
        ></div>

        <div class="download-list-item-buttons">
          <a
            v-for="(link, title) in item.buttons"
            :href="link === '' ? '#' : link"
            class="btn"
            target="_blank"
            v-html="title"
            @click="onClickLink"
          ></a>
        </div>
    </li>


    <li class="download-list-item--fluid">

      <div class="static-text-title">
        <p>
          Transfer of rights to use the software produced by XSQUARE LLC for commercial purposes is carried out on the basis of the
          <a href="https://lcdp.xsquare.ru/files/law/X2-License-Agreement-Public-Offer-2024-v7a.pdf" target="_blank" class="link">License Agreement</a>.
        </p>
        <p>
          Licenses to use the software must be purchased from <nuxt-link to="/buy" class="link">official partners</nuxt-link>.
        </p>
      </div>
    </li>
  </ul>

  <modal ref="downloadModal">
    <template #content>
      <p style="text-align: center;display:flex;align-items: center;justify-content: center;min-height: 150px">
        Ожидайте, скоро появиться в открытом доступе
      </p>
    </template>
  </modal>
</template>

<style scoped lang="scss">
.download-list {
  padding-top: 60px;
  --gap: 40px;
  --items-count: 3;
  display: flex;
  flex-wrap: wrap;
  gap: var(--gap);
  @include onMD {
    --gap: 30px;
    --items-count: 2;
    padding-top: 50px;
  }
  @include onXS {
    --gap: 25px;
    --items-count: 1;
    padding-top: 30px;
  }

  &-item {
    width: calc((100% / var(--items-count)) - (var(--gap) - (var(--gap) / var(--items-count))));
    border-radius: var(--main-border-radius);
    background: $GRAY_TEXT_LIGHT;
    padding: 25px;
    flex-grow: 1;
    text-align: center;
    color: $GRAY_TEXT_DARK;
    display: flex;
    flex-direction: column;
    align-items: center;
    @include onMD {
      padding: 20px;
    }
    @include onXS {
      padding: 15px;
    }

    &--fluid {
      @extend .download-list-item;
      align-items: flex-start;
      width: calc(100% - (100% / var(--items-count)) - (var(--gap) - (var(--gap) / (var(--items-count) - 1.3) )));
    }

    &-icon {
      width: 67.5px;
      margin: auto auto 25px;
      @include onMD {
        width: 55px;
      }
      @include onXS {
        width: 50px;
      }

      img {
        width: 100%;
        user-select: none;
      }
    }

    &-title {
      color: $GRAY_TEXT_DARK;
      @include font-size(20px);
      --min-font-size: 18px;
      font-weight: 700;
      line-height: 1.2em;
      letter-spacing: -0.8px;
      margin-bottom: 20px;
    }

    &-stats {
      display: flex;
      gap: 30px;
      flex-direction: row;
      justify-content: space-between;
      margin-bottom: 20px;
      max-width: 400px;
      @include onLG {
        gap: 20px;
        flex-wrap: wrap;
        white-space: nowrap;
        justify-content: center;
      }

      li {
        display: flex;
        flex-direction: column;
        gap: 4px;
        @include font-size(16px);
        --min-font-size: 14px;
        font-weight: 700;
        line-height: 1.2em;
        letter-spacing: -0.64px;

        .value {
          color: rgba(47, 55, 90, 0.80);
          @include font-size(16px);
          --min-font-size: 14px;
          font-weight: 500;
          line-height: 1.2em;
          letter-spacing: -0.64px;
        }
      }
    }

    &-subtitle {
      @include font-size(16px);
      --min-font-size: 14px;
      font-weight: 700;
      line-height: 1.2em;
      letter-spacing: -0.64px;
      margin-bottom: 4px;
    }

    &-description {
      color: rgba(47, 55, 90, 0.80);
      @include font-size(13px);
      --min-font-size: 12px;
      font-weight: 500;
      line-height: 1.2em;
      letter-spacing: -0.52px;
    }

    &-buttons {
      margin-top: 25px;
      display: flex;
      gap: 20px;
      flex-wrap: wrap;
      justify-content: center;
      width: 100%;

      a {
        display: flex;
        width: 150px;
        padding: 15px 30px;
        justify-content: center;
        align-items: center;
        gap: 10px;
        border-radius: 100px;
        background: $VIOLET;
        color: #fff;
        text-decoration: none;
        @include onLG {
          padding: 12px 25px;
          width: 100%;
        }
        @include onMD {
          padding: 10px 20px;
        }
      }
    }

    .static-text-title {
      text-align: left;
      @include font-size(18px);
      --min-font-size: 16px;
      font-weight: 600;
      line-height: 1.2em;
      letter-spacing: -0.72px;
      &:has(+*) {
        margin-bottom: 20px;
      }
    }

    .static-text-description {
      text-align: left;
      color: rgba(47, 55, 90, 0.80);
      @include font-size(14px);
      --min-font-size: 13px;
      font-weight: 400;
      line-height: 1.2em;
      letter-spacing: -0.56px;
      p {
        margin-bottom: 20px;

        &:last-child {
          margin-bottom: 0px;
        }
      }
    }
  }
}
</style>
